import { useEffect, useState } from 'react';

import { Divider, TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';

import { PageTitle } from '~/components/common';
import { PageWrapper } from '~/components/layout';
import { ReceiveReportDetailModal, ReceiveReportFilter, ReceiveReportTable } from '~/components/receive';

import { useDownloadReady, useModal, useToggle } from '~/hooks/common';
import {
  receiveKeys,
  useGetFilterAllQuery,
  useGetReportHistoriesByReportIdMutation,
  useGetReportHistoriesDownloadMutation,
  useGetReportHistoriesQuery,
} from '~/hooks/receive';
import { useUserInfoStore } from '~/store';
import { addIf, formatDate, getDateByOffset, isValidDateRange, renderFilterErrorMessage } from '~/utils';

import { BREAD_CRUMB, OPTION_ALL, PATH, TOAST_MESSAGE } from '~/constants';
import { INITIAL_RECEIVE_PAGE_CURSOR, INITIAL_RECEIVE_PAGE_SIZE, MAX_RECEIVE_DATE_RANGE } from '~/constants/receive';

import type {
  TypeGetReceivingReportHistoriesParams,
  TypeReceiveReportFilter,
  TypeReceiveReportFilterStatus,
  TypeReceiveReportHistory,
  TypeReceiveReportHistoryDetail,
} from '~/types/receive.types';

const DEFAULT_SEARCH_FILTER_VALUES: TypeReceiveReportFilter = {
  keywordType: null,
  keyword: '',
  cluster: OPTION_ALL,
  warehouse: OPTION_ALL,
  dateType: null,
  date: { start: getDateByOffset(-7, 'day'), end: new Date() },
  releaseCode: OPTION_ALL,
  reportStatus: OPTION_ALL,
  workType: OPTION_ALL,
  pagination: {
    currentPage: INITIAL_RECEIVE_PAGE_CURSOR,
    totalCount: 0,
    pageSize: INITIAL_RECEIVE_PAGE_SIZE,
  },
};

const DEFAULT_SEARCH_FILTER_VALUES_STATE: TypeReceiveReportFilterStatus = {
  date: 'default',
};

/**
 * [입고관리 > 입고 리포트 관리] 페이지 컴포넌트
 */
const ReceiveReportPage = () => {
  const queryClient = useQueryClient();

  const reportHistoriesDownloadMutation = useGetReportHistoriesDownloadMutation();
  const reportDetailMutation = useGetReportHistoriesByReportIdMutation();

  const [searchFilterValues, setSearchFilterValues] = useState<TypeReceiveReportFilter>({
    ...DEFAULT_SEARCH_FILTER_VALUES,
  });
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<TypeReceiveReportFilterStatus>(
    DEFAULT_SEARCH_FILTER_VALUES_STATE
  );
  const [reportDetailData, setReportDetailData] = useState<TypeReceiveReportHistoryDetail | null>(null);

  const { ownerCode } = useUserInfoStore((state) => state.userInfo);

  const { data: filterOptions, isError: isFilterOptionsError } = useGetFilterAllQuery('REPORT_HISTORY', {
    silent: true,
  });

  const [listQueryParams, setListQueryParams] = useState<TypeGetReceivingReportHistoriesParams | null>(null);
  const listQuery = useGetReportHistoriesQuery({
    ownerCode,
    params: listQueryParams ?? ({} as TypeGetReceivingReportHistoriesParams),
    enabled: !!listQueryParams && !!ownerCode,
  });

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const { isDownloadReady, searchedParam, markDownloadReady, checkFilterChange } = useDownloadReady<
    TypeReceiveReportFilter,
    TypeGetReceivingReportHistoriesParams
  >({
    initialFilter: DEFAULT_SEARCH_FILTER_VALUES,
  });

  const reportDetailModal = useModal();

  useEffect(() => {
    if (filterOptions) {
      setSearchFilterValues((prev) => ({
        ...prev,
        keywordType: filterOptions.keywordType[0],
        dateType: filterOptions.dateType[0],
      }));
    }
  }, [filterOptions]);

  useEffect(() => {
    if (isFilterOptionsError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [isFilterOptionsError]);

  useEffect(() => {
    checkFilterChange(searchFilterValues);
  }, [searchFilterValues]);

  useEffect(() => {
    if (listQuery.data && listQueryParams && !listQuery.isPlaceholderData) {
      markDownloadReady({
        filterValue: searchFilterValues,
        hasData: listQuery.data.data.reportHistories.length > 0,
        searchedParam: listQueryParams,
      });
    }
  }, [listQuery.data, listQueryParams, listQuery.isPlaceholderData]);

  const handleSearch = (filterValues: TypeReceiveReportFilter = searchFilterValues) => {
    if (!isValidDateRange(filterValues.date, MAX_RECEIVE_DATE_RANGE)) {
      setSearchFilterValuesStatus((prev) => ({ ...prev, date: 'error' }));

      dialogService.addDialog({ children: `최대 ${MAX_RECEIVE_DATE_RANGE}일 기간으로 입력 후 검색해주세요.` });
      return;
    }

    const params: TypeGetReceivingReportHistoriesParams = {
      ...addIf(filterValues.keyword, 'codeType', filterValues.keywordType?.value),
      ...addIf(filterValues.keyword, 'code', filterValues.keyword),
      ...addIf(filterValues.cluster.value, 'cluster'),
      ...addIf(filterValues.warehouse.value, 'warehouse'),
      dateType: filterValues.dateType?.value ?? '',
      dateFrom: formatDate(filterValues.date?.start, 'YYYY-MM-DD') ?? '',
      dateTo: formatDate(filterValues.date?.end, 'YYYY-MM-DD') ?? '',
      ...addIf(filterValues.releaseCode.value, 'releaseCode'),
      ...addIf(filterValues.reportStatus.value, 'reportStatus'),
      ...addIf(filterValues.workType.value, 'taskCode'),
      limit: filterValues.pagination.pageSize,
      offset: filterValues.pagination.currentPage * filterValues.pagination.pageSize,
    };

    queryClient.invalidateQueries({
      queryKey: receiveKeys.reportHistories(ownerCode, params),
    });

    setSearchFilterValues(filterValues);
    setListQueryParams(params);
  };

  const handleResetFilter = () => {
    setSearchFilterValues({
      ...DEFAULT_SEARCH_FILTER_VALUES,
      pagination: searchFilterValues.pagination,
      keywordType: filterOptions?.keywordType[0] ?? null,
      dateType: filterOptions?.dateType[0] ?? null,
    });
    setSearchFilterValuesStatus(DEFAULT_SEARCH_FILTER_VALUES_STATE);
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_RECEIVE_PAGE_CURSOR, pageSize: value.value },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: value },
    });
  };

  const handleReceiveReportExcelDownload = () => {
    if (!searchedParam) return;

    reportHistoriesDownloadMutation.mutate(
      { ownerCode, ...searchedParam },
      {
        onSuccess: () => {
          toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
        },
      }
    );
  };

  const handleViewDetail = (data: TypeReceiveReportHistory) => {
    reportDetailMutation.mutate(
      { ownerCode, reportId: String(data.id) },
      {
        onSuccess: ({ data: detailData }) => {
          setReportDetailData(detailData);
          reportDetailModal.handleOpenModal();
        },
      }
    );
  };

  return (
    <PageWrapper>
      <PageTitle
        title="입고 리포트 관리"
        breadcrumbs={BREAD_CRUMB[PATH.RECEIVE_REPORT]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <div>
          <ReceiveReportFilter
            options={filterOptions}
            values={searchFilterValues}
            valuesState={searchFilterValuesStatus}
            onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
            onChangeStatus={(values) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...values }))}
            onSearch={() =>
              handleSearch({
                ...searchFilterValues,
                pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_RECEIVE_PAGE_CURSOR },
              })
            }
            onReset={handleResetFilter}
          />
          <Divider color="gray100" />
        </div>
      )}
      <ReceiveReportTable
        data={listQuery.data?.data.reportHistories ?? null}
        pagination={{
          ...searchFilterValues.pagination,
          totalCount: listQuery.data?.pagination.listTotal ?? 0,
        }}
        isDownloadReady={isDownloadReady}
        onClickReceiveReportExcelDownload={handleReceiveReportExcelDownload}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
        onClickViewDetail={handleViewDetail}
      />
      {reportDetailModal.isOpen && reportDetailData && (
        <ReceiveReportDetailModal
          open={reportDetailModal.isOpen}
          data={reportDetailData}
          onClose={reportDetailModal.handleCloseModal}
        />
      )}
    </PageWrapper>
  );
};

export default ReceiveReportPage;
